import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useApi } from '../../../hooks/useApi/useApi';
import { useDictionary } from '../../../hooks/useDictionary/useDictionary';
import { useAnalytics } from '../../../providers/AnalyticsProvider/AnalyticsProvider';
import { useAuth } from '../../../providers/AuthProvider/AuthProvider';
import DumbPairingTemplate, { PairingStep } from './PairingTemplate.dumb';

/* -------------------------------- Component ------------------------------- */

/**
 * PairingTemplate
 *
 * @returns JSX.Element
 */
const PairingTemplate = () => {
    const api = useApi();
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();

    const pairingCodeSuccessText = useDictionary('pairing.success.subtitle');
    const pairingCodeSuccessTitle = useDictionary('pairing.success.title');

    // Retrieve pairing code from URL - if user has used QR code to avoid code entry
    const { pairingCode } = useParams<{ pairingCode?: string }>();

    // Checks if this is a return to the pairing page for success message
    const location = useLocation();
    const state = location.state as { success?: boolean };

    const { updatePageObject } = useAnalytics();

    // Determine what pairing step should show
    const [step, setStep] = useState(() => {
        // If pairing has been successful
        if (state?.success) {
            // Remove flag from history state to enable users to pair again
            window.history.replaceState({}, document.title);

            return PairingStep.Success;
        }

        // If pairing code is in URL show sign in options otherwise show input for pairing code
        return pairingCode ? PairingStep.Redirect : PairingStep.EnterCode;
    });

    // Store the code if it exists or enable state for code to be store via keyboard input
    const [code, setCode] = useState(pairingCode || '');

    // Callback to pair an already logged in user
    const pairLoggedInUser = async () => {
        const pairResult = await api.pairDevice(code.toLowerCase());

        if (!pairResult.ok) {
            // @TODO: Error Handling
            // eslint-disable-next-line no-console
            console.trace(pairResult);
            return;
        }

        if (pairResult.payload.body.wantsToPurchase) {
            // IDs are delivered namespaced by the item type - for now that is limited to Products but in the future
            // could include Collections

            // track successful sign in before redirect
            window.analytics.trigger({
                type: 'pairing.completed',
                wantsToPurchase: pairResult.payload.body.wantsToPurchase,
            });

            const url = pairResult.payload.body.wantsToPurchase
                .split('_')
                .join('/');

            navigate(`/${url}/pay`);
        } else {
            // track successful sign in before redirect
            window.analytics.trigger({
                type: 'pairing.completed',
            });
            setStep(PairingStep.Success);
        }
    };

    const validateToken = useCallback(
        async (passedToken: string) => {
            const result = await api.validatePairingCode(
                passedToken.toLowerCase(),
            );

            if (!result.ok) return result;

            // track successful sign in before redirect
            window.analytics.trigger({
                type: 'pairing.submitted',
            });

            setCode(passedToken);
            setStep(PairingStep.Redirect);

            return result;
        },
        [api],
    );

    useEffect(() => {
        updatePageObject({
            pageId: `pair_${step}`,
            pageName: `Pairing: ${step}`,
            pageType: 'system',
        });
    }, [step, updatePageObject]);

    return (
        <DumbPairingTemplate
            code={code}
            validateToken={validateToken}
            pairLoggedInUser={pairLoggedInUser}
            step={step}
            isAuthenticated={isAuthenticated}
            labels={{ pairingCodeSuccessText, pairingCodeSuccessTitle }}
        />
    );
};

/* --------------------------------- Export --------------------------------- */

export default PairingTemplate;
